import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgSelectModule } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColorChromeModule } from 'ngx-color/chrome';
import { BehaviorSubject, Observable, merge, of, shareReplay, switchMap, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';

import { debounceSafeDefault } from '@shared/constants/debounce-time-safe-default.constant';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { StopPropagationDirective } from '@shared/directives/stop-propagation.directive';
import { TooltipDirectiveModule } from '@shared/directives/tooltip.directive';
import { EIcon } from '@shared/enums/icon.enum';
import { PipesModule } from '@shared/pipes/pipes.module';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { InputErrorModule } from '@ui-components/components/input-error/input-error.module';
import { CustomControlAbstract } from '@ui-components/controls/custom-control.abstract';

import { InputModule } from '../input/input.module';
import { RadioButtonModule } from '../radio-button/radio-button.module';

const defaultCloseDelay = 1000;

@UntilDestroy()
@Component({
  selector: 'app-custom-color-picker',
  templateUrl: './custom-color-picker.component.html',
  styleUrls: ['./custom-color-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    RadioButtonModule,
    ReactiveFormsModule,
    IconComponent,
    MatButtonModule,
    InputModule,
    NgSelectModule,
    PipesModule,
    InputErrorModule,
    MatProgressSpinnerModule,
    ColorChromeModule,
    StopPropagationDirective,
    ClickOutsideDirective,
    TooltipDirectiveModule,
  ],
})
export class CustomColorPickerComponent extends CustomControlAbstract<any> implements OnInit, ControlValueAccessor {
  labelOutsideDefaultCss = 'nowrap body-small-bold';

  private _isOpened$ = new BehaviorSubject(false); // TODO change to signals after Angular 16+
  private _isOpenedTrigger$ = new BehaviorSubject(false); // TODO change to signals after Angular 16+
  private _forceIsOpen$ = new BehaviorSubject(false); // TODO change to signals after Angular 16+
  isOpened$ = this.initIsOpen();

  @Input() selectCss = 'body-small text-color dark';

  @Input() set labelCss(value: string) {
    this._labelCss = value;
  }

  get labelCss(): string {
    if (this._labelCss) return this._labelCss;

    return this.labelOutsideDefaultCss;
  }

  private _valueCss: string;
  @Input() set valueCss(value) {
    this._valueCss = value;
  }

  get valueCss() {
    if (this._valueCss) {
      return this._valueCss;
    }
    return 'ellipsis text-color dark body-small';
  }

  @Input() containerCss = 'display-flex align-items-center';
  @Input() label = '';
  @Input() icon: EIcon;
  @Input() iconTooltip = '';
  @Input() attrPlaceholder = '';
  @Input() attrDisable = false;
  @Input() markAsInvalid = false;
  @Input() errorSection = true;
  @Input() isTextMode = false;

  _labelCss = '';
  _labelElement: ElementRef<HTMLElement>;

  @ViewChild('labelElement', { static: false }) set content(value: ElementRef<HTMLElement>) {
    if (!value) return;

    this._labelElement = value;
    this.cdr.detectChanges();
  }

  constructor(
    @Self() @Optional() protected ngControl: NgControl,
    protected cdr: ChangeDetectorRef,
    @Optional() formDirective: FormGroupDirective
  ) {
    super(ngControl, cdr, formDirective);
  }

  ngOnInit(): void {
    this.initControlBase();

    this.control.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(value => this.onChanged(value)),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  handleOpenState(isOpen: boolean, isMouseMove = false, isForce = false): void {
    if (isForce) {
      this._forceIsOpen$.next(isOpen);
      this._isOpened$.next(isOpen);
    }

    if (!this._isOpened$.value && isMouseMove) {
      return;
    }

    if (isMouseMove) {
      this._isOpenedTrigger$.next(isOpen);
      return;
    }

    this._isOpened$.next(isOpen);
  }

  writeValue(value: any): void {
    if (value != this.control.value) {
      this.control.setValue(value);
    }
  }

  private initIsOpen(): Observable<boolean> {
    return merge(
      merge(this._isOpened$, this._isOpenedTrigger$).pipe(
        distinctUntilChanged(),
        debounceTime(debounceSafeDefault),
        switchMap(isOpened => {
          return isOpened
            ? of(isOpened)
            : timer(defaultCloseDelay).pipe(
                tap(() => this._isOpened$.next(false)),
                map(() => isOpened)
              );
        }),
        distinctUntilChanged()
      ),
      this._forceIsOpen$
    ).pipe(distinctUntilChanged(), shareReplay(1));
  }
}
